import { OfferType, SearchListFilters } from '@/types';
import { pushObjectSearchStart } from './dataLayer';

/**
 * Payload builders for GA4 `object_search_start` from a housing list request body.
 * Uses German labels (`Kauf` / `Miete`) to match the dataLayer spec.
 */

function offerTypeToKaufMiete(offerType: OfferType | undefined): 'Kauf' | 'Miete' {
  return offerType === OfferType.RENT ? 'Miete' : 'Kauf';
}

/**
 * Map viewport bbox must not count as a new search (pan/zoom spam).
 * Drawn polygon geometry still does.
 */
function mapAreaForObjectSearchSignature(
  mapArea: NonNullable<SearchListFilters>['mapArea'],
): { lonLatGeometry: [number, number][] } | undefined {
  if (!mapArea?.lonLatGeometry?.length) return undefined;
  return { lonLatGeometry: mapArea.lonLatGeometry };
}

/**
 * Signature of a housing list search for `object_search_start` dedup.
 * Ignores pagination and viewport `mapArea`; keeps polygon geometry and the rest of the body.
 */
export function getObjectSearchStartSignature(
  request: SearchListFilters | undefined,
): string | null {
  if (!request) return null;
  const {
    currentPageIndex: _currentPageIndex,
    itemsPerPage: _itemsPerPage,
    mapArea,
    ...rest
  } = request;
  return JSON.stringify({
    ...rest,
    mapArea: mapAreaForObjectSearchSignature(mapArea),
  });
}

export type ObjectSearchSource = 'list' | 'map';

/**
 * Maps `/search/propileya` request fields to flat `object_search_start` payload keys.
 * Price bounds follow the active offer type (sell vs rent).
 * `objectCategory` is the German UI titles string when the caller has them.
 * `searchSource` is `list` for `/search/propileya` and `map` for `/search/map/propileya`.
 */
export function buildObjectSearchStartPayload(
  request: NonNullable<SearchListFilters>,
  objectCategory?: string,
  searchSource?: ObjectSearchSource,
): Record<string, unknown> {
  const isRent = request.offerType === OfferType.RENT;
  const priceMin = isRent ? request.rentPriceFrom : request.priceFrom;
  const priceMax = isRent ? request.rentPriceTo : request.priceTo;

  return {
    object_type: offerTypeToKaufMiete(request.offerType),
    object_category: objectCategory ?? request.categories?.join(', ') ?? '',
    object_price_min: priceMin ?? undefined,
    object_price_max: priceMax ?? undefined,
    object_rooms_min: request.roomNumberFrom ?? undefined,
    object_rooms_max: request.roomNumberTo ?? undefined,
    search_source: searchSource,
  };
}

/** Fires `object_search_start` for one `/search/map` request. Viewport-only repeats are ignored. */
export function pushMapObjectSearchStart(
  lastSigRef: { current: string | null },
  request: SearchListFilters | undefined,
  objectCategory: string,
) {
  if (!request) return;
  const sig = getObjectSearchStartSignature(request);
  if (sig === null || sig === lastSigRef.current) return;
  lastSigRef.current = sig;
  pushObjectSearchStart(buildObjectSearchStartPayload(request, objectCategory, 'map'));
}
